import React, { useEffect, useRef, useState } from 'react';
import { debounce } from '../../utils/debounce';
import * as CSS from './elements/StudentAssessment';

// common autosave timing: save 1s after typing stops, at least every 5s while typing
export const AUTOSAVE_WAIT = 1000;
export const AUTOSAVE_MAX_WAIT = 5000;
const RETRY_DELAYS = [1000, 2000, 4000];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// One learning plan answer that saves itself. onSave(questionId, text) writes it;
// onStatusChange('saving' | 'saved' | 'error') reports progress to the page.
const AutosaveAnswer = ({ question, index, onSave, onStatusChange }) => {
  const [value, setValue] = useState(question.answer || '');
  const isFocused = useRef(false);
  const lastSaved = useRef(question.answer || '');
  const callbacks = useRef({ onSave, onStatusChange });
  callbacks.current = { onSave, onStatusChange };

  const saver = useRef(null);
  if (!saver.current) {
    saver.current = debounce(
      async (text) => {
        if (text === lastSaved.current) return;
        callbacks.current.onStatusChange('saving');
        for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
          try {
            await callbacks.current.onSave(question.id, text);
            lastSaved.current = text;
            callbacks.current.onStatusChange('saved');
            return;
          } catch (error) {
            if (attempt === RETRY_DELAYS.length) {
              callbacks.current.onStatusChange('error');
              return;
            }
            await wait(RETRY_DELAYS[attempt]);
          }
        }
      },
      AUTOSAVE_WAIT,
      { maxWait: AUTOSAVE_MAX_WAIT }
    );
  }

  // take in changes made elsewhere (e.g. by a teacher), but never while the student
  // is typing in this field or has an unsaved change in it
  useEffect(() => {
    const incoming = question.answer || '';
    if (!isFocused.current && !saver.current.isPending()) {
      setValue(incoming);
      lastSaved.current = incoming;
    }
  }, [question.answer]);

  // save anything pending when the page is left or the tab is closed
  useEffect(() => {
    const flush = () => saver.current.flush();
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  const id = `questions.${index}.answer`;

  return (
    <CSS.AnswerLi isComplete={!!value}>
      <CSS.StudentQuestion htmlFor={id} isComplete={!!value}>
        {index + 1}. {question.text}
        {question.teacherOnly && <CSS.LockIcon style={{ fontSize: '1em' }} />}
      </CSS.StudentQuestion>
      <CSS.AssessmentTextArea
        data-testid="StudentAssessment-answer"
        id={id}
        name={id}
        rowsMin={3}
        style={{ gridArea: 'answer' }}
        disabled={question.teacherOnly}
        value={value}
        onFocus={() => {
          isFocused.current = true;
        }}
        onChange={(e) => {
          setValue(e.target.value);
          saver.current(e.target.value);
        }}
        onBlur={() => {
          isFocused.current = false;
          saver.current.flush();
        }}
      />
    </CSS.AnswerLi>
  );
};

export default AutosaveAnswer;
